import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Calendar, ArrowLeft, Plus, Edit, Trash2, Clock, 
  Bus, MapPin, Banknote, X, Save, ChevronRight
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import api from '../../utils/api';

const BusOperatorSchedules = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [schedules, setSchedules] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [buses, setBuses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  
  const [formData, setFormData] = useState({
    route_id: '',
    bus_id: '',
    departure_time: '06:00',
    days_of_week: [1, 2, 3, 4, 5, 6, 7],
    valid_from: '',
    valid_until: '',
    pricing: {
      standard: '',
      business: '',
      vip: ''
    }
  });

  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  useEffect(() => {
    const token = localStorage.getItem('tekstar_token');
    if (!token) {
      navigate('/bus/operator/login');
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [schedulesRes, routesRes, busesRes] = await Promise.all([
        api.get('/bus/operators/schedules'),
        api.get('/bus/operators/routes'),
        api.get('/bus/operators/fleet')
      ]);
      
      setSchedules(schedulesRes.data.schedules || []);
      setRoutes(routesRes.data.routes || []);
      setBuses(busesRes.data.buses || []);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('tekstar_token');
        localStorage.removeItem('tekstar_user');
        navigate('/bus/operator/login');
      } else if (error.response?.status === 404) {
        navigate('/bus/operator/apply');
      } else {
        console.error('Failed to load data:', error);
        toast({
          title: 'Error',
          description: error.response?.data?.detail || 'Failed to load data',
          variant: 'destructive'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      ...formData,
      pricing: {
        standard: parseFloat(formData.pricing.standard),
        business: parseFloat(formData.pricing.business),
        vip: parseFloat(formData.pricing.vip)
      }
    };

    try {
      if (editingSchedule) {
        await api.put(`/bus/operators/schedules/${editingSchedule.id}`, payload);
        toast({ title: 'Success', description: 'Schedule updated successfully' });
      } else {
        await api.post('/bus/operators/schedules', payload);
        toast({ title: 'Success', description: 'Schedule created successfully' });
      }
      
      setShowForm(false);
      setEditingSchedule(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save schedule',
        variant: 'destructive'
      });
    }
  };

  const handleEdit = (schedule) => {
    setEditingSchedule(schedule);
    setFormData({
      route_id: schedule.routeId,
      bus_id: schedule.busId,
      departure_time: schedule.departureTime,
      days_of_week: schedule.daysOfWeek || [1, 2, 3, 4, 5, 6, 7],
      valid_from: schedule.validFrom,
      valid_until: schedule.validUntil,
      pricing: schedule.pricing || { standard: '', business: '', vip: '' }
    });
    setShowForm(true);
  };

  const toggleDay = (day) => {
    setFormData(prev => ({
      ...prev,
      days_of_week: prev.days_of_week.includes(day)
        ? prev.days_of_week.filter(d => d !== day)
        : [...prev.days_of_week, day].sort((a, b) => a - b)
    }));
  };

  const resetForm = () => {
    const today = new Date().toISOString().split('T')[0];
    const threeMonthsLater = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    
    setFormData({
      route_id: '',
      bus_id: '',
      departure_time: '06:00',
      days_of_week: [1, 2, 3, 4, 5, 6, 7],
      valid_from: today,
      valid_until: threeMonthsLater,
      pricing: { standard: '', business: '', vip: '' }
    });
  };

  const handleRouteChange = (routeId) => {
    const route = routes.find(r => r.id === routeId);
    if (route) {
      setFormData(prev => ({
        ...prev,
        route_id: routeId,
        pricing: {
          standard: route.basePrice?.toString() || '',
          business: Math.round(route.basePrice * 1.4)?.toString() || '',
          vip: Math.round(route.basePrice * 2)?.toString() || ''
        }
      }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/bus/operator">
                <ArrowLeft className="h-6 w-6" />
              </Link>
              <div>
                <h1 className="text-xl font-bold">Schedule Management</h1>
                <p className="text-indigo-200 text-sm">{schedules.length} schedule(s)</p>
              </div>
            </div>
            <Button 
              onClick={() => { setShowForm(true); setEditingSchedule(null); resetForm(); }}
              className="bg-white text-indigo-600 hover:bg-indigo-50"
              disabled={routes.length === 0 || buses.length === 0}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Schedule
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4">
        {/* Prerequisites Warning */}
        {(routes.length === 0 || buses.length === 0) && (
          <Card className="p-4 mb-4 bg-yellow-50 border-yellow-200">
            <p className="text-yellow-800">
              {routes.length === 0 && buses.length === 0 
                ? 'Please add buses and routes first before creating schedules.'
                : routes.length === 0 
                  ? 'Please add at least one route first.'
                  : 'Please add at least one bus first.'
              }
            </p>
            <div className="flex gap-2 mt-2">
              {buses.length === 0 && (
                <Link to="/bus/operator/fleet">
                  <Button size="sm" variant="outline">Add Bus</Button>
                </Link>
              )}
              {routes.length === 0 && (
                <Link to="/bus/operator/routes">
                  <Button size="sm" variant="outline">Add Route</Button>
                </Link>
              )}
            </div>
          </Card>
        )}

        {/* Add/Edit Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">
                  {editingSchedule ? 'Edit Schedule' : 'Create New Schedule'}
                </h2>
                <button onClick={() => { setShowForm(false); setEditingSchedule(null); }}>
                  <X className="h-6 w-6 text-gray-400" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Route Selection */}
                <div>
                  <Label className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    Route *
                  </Label>
                  <select
                    value={formData.route_id}
                    onChange={(e) => handleRouteChange(e.target.value)}
                    className="w-full mt-1 p-2 border rounded-md"
                    required
                  >
                    <option value="">Select a route</option>
                    {routes.map((route) => (
                      <option key={route.id} value={route.id}>
                        {route.origin?.city} → {route.destination?.city} (K{route.basePrice})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Bus Selection */}
                <div>
                  <Label className="flex items-center gap-2">
                    <Bus className="h-4 w-4" />
                    Bus *
                  </Label>
                  <select
                    value={formData.bus_id}
                    onChange={(e) => setFormData({ ...formData, bus_id: e.target.value })}
                    className="w-full mt-1 p-2 border rounded-md"
                    required
                  >
                    <option value="">Select a bus</option>
                    {buses.filter(b => b.status === 'active').map((bus) => (
                      <option key={bus.id} value={bus.id}>
                        {bus.registrationNumber} - {bus.make} {bus.model} ({bus.capacity} seats)
                      </option>
                    ))}
                  </select>
                </div>

                {/* Departure Time */}
                <div>
                  <Label className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Departure Time *
                  </Label>
                  <Input
                    type="time"
                    value={formData.departure_time}
                    onChange={(e) => setFormData({ ...formData, departure_time: e.target.value })}
                    required
                  />
                </div>

                {/* Days of Week */}
                <div>
                  <Label>Operating Days</Label>
                  <div className="flex gap-2 mt-2">
                    {dayNames.map((day, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => toggleDay(idx + 1)}
                        className={`w-10 h-10 rounded-full text-sm font-medium transition-all ${
                          formData.days_of_week.includes(idx + 1)
                            ? 'bg-indigo-600 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {day}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Validity Period */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Valid From *</Label>
                    <Input
                      type="date"
                      value={formData.valid_from}
                      onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>Valid Until *</Label>
                    <Input
                      type="date"
                      value={formData.valid_until}
                      onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                      required
                    />
                  </div>
                </div>

                {/* Pricing */}
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Banknote className="h-4 w-4" />
                    Seat Pricing (K)
                  </Label>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs text-gray-500">Standard</Label>
                      <Input
                        type="number"
                        value={formData.pricing.standard}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          pricing: { ...formData.pricing, standard: e.target.value }
                        })}
                        placeholder="250"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-purple-600">Business</Label>
                      <Input
                        type="number"
                        value={formData.pricing.business}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          pricing: { ...formData.pricing, business: e.target.value }
                        })}
                        placeholder="350"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-yellow-600">VIP</Label>
                      <Input
                        type="number"
                        value={formData.pricing.vip}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          pricing: { ...formData.pricing, vip: e.target.value }
                        })}
                        placeholder="500"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => { setShowForm(false); setEditingSchedule(null); }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 bg-indigo-600 hover:bg-indigo-700">
                    <Save className="h-4 w-4 mr-2" />
                    {editingSchedule ? 'Update' : 'Create'} Schedule
                  </Button>
                </div>
              </form>
            </Card>
          </div>
        )}

        {/* Schedules List */}
        {schedules.length === 0 ? (
          <Card className="p-8 text-center">
            <Calendar className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No Schedules Yet</h3>
            <p className="text-gray-500 mb-4">Create schedules to start accepting bookings</p>
            {routes.length > 0 && buses.length > 0 && (
              <Button 
                onClick={() => { setShowForm(true); resetForm(); }}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Schedule
              </Button>
            )}
          </Card>
        ) : (
          <div className="space-y-4">
            {schedules.map((schedule) => (
              <Card key={schedule.id} className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl font-bold text-indigo-600">{schedule.departureTime}</span>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        schedule.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {schedule.status}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2 text-gray-600 mb-2">
                      <span>{schedule.route?.origin?.city}</span>
                      <ChevronRight className="h-4 w-4" />
                      <span>{schedule.route?.destination?.city}</span>
                    </div>

                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Bus className="h-4 w-4" />
                        {schedule.bus?.registrationNumber}
                      </span>
                      <span>
                        {schedule.daysOfWeek?.map(d => dayNames[d - 1]).join(', ')}
                      </span>
                    </div>

                    <div className="flex gap-3 mt-2 text-sm">
                      <span className="px-2 py-1 bg-gray-100 rounded">Standard: K{schedule.pricing?.standard}</span>
                      <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded">Business: K{schedule.pricing?.business}</span>
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded">VIP: K{schedule.pricing?.vip}</span>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleEdit(schedule)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BusOperatorSchedules;
